import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

// ============================================================================
// MAP STORE - Source unique de vérité pour l'état de la carte
// ============================================================================
// Ce store centralise tout l'état de la carte MapLibre.
// Il remplace les multiples useState/useRef dispersés dans MapLibreView.
//
// Responsabilités :
// - Position de la carte (center, zoom)
// - État d'initialisation (isReady)
// - Animations (isAnimating, pendingFlyTo)
// - Bouton "Search in this area" (lastSearchCenter, shouldShowSearchButton)
//
// Le locationStore reste responsable de la position UTILISATEUR.
// Le mapStore est responsable de comment la CARTE affiche cette position.
// ============================================================================

// Constantes centralisées - Une seule source de vérité
// Note: Typage explicite pour éviter les types littéraux avec TS 5.x
export const MAP_CONFIG: {
  DEFAULT_ZOOM: number;
  MAX_ZOOM: number;
  FLY_DURATION: number;
  SEARCH_DISTANCE_THRESHOLD: number;
} = {
  DEFAULT_ZOOM: 15,
  MAX_ZOOM: 18,
  FLY_DURATION: 1200, // Durée des animations flyTo (ms)
  SEARCH_DISTANCE_THRESHOLD: 2, // Distance en km pour afficher "Search in this area"
};

export interface MapCenter {
  lat: number;
  lng: number;
}

interface MapState {
  // État de la carte
  center: MapCenter | null;
  zoom: number;
  isReady: boolean;
  isAnimating: boolean;

  // Animation en attente (pour que MapLibreView l'exécute)
  pendingFlyTo: MapCenter | null;
  pendingJumpTo: MapCenter | null;

  // Bouton "Search in this area"
  lastSearchCenter: MapCenter | null;
  shouldShowSearchButton: boolean;

  // Actions
  flyTo: (lat: number, lng: number, zoom?: number) => void;
  jumpTo: (lat: number, lng: number, zoom?: number) => void;
  setCenter: (lat: number, lng: number) => void;
  setZoom: (zoom: number) => void;
  setReady: (ready: boolean) => void;
  setSearchCenter: (lat: number, lng: number) => void;
  updateSearchButtonVisibility: (currentLat: number, currentLng: number) => void;

  // Actions internes (appelées par MapLibreView après l'animation)
  clearPendingFlyTo: () => void;
  clearPendingJumpTo: () => void;
  setAnimating: (animating: boolean) => void;
}

// Fonction utilitaire pour calculer la distance entre deux points (Haversine simplifié)
const calculateDistanceKm = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371; // Rayon de la Terre en km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const useMapStore = create<MapState>()((set, get) => ({
  // État initial
  center: null,
  zoom: MAP_CONFIG.DEFAULT_ZOOM,
  isReady: false,
  isAnimating: false,
  pendingFlyTo: null,
  pendingJumpTo: null,
  lastSearchCenter: null,
  shouldShowSearchButton: false,

  // ============================================================================
  // ACTIONS PRINCIPALES
  // ============================================================================

  /**
   * Déclenche une animation flyTo vers une position.
   * MapLibreView écoute `pendingFlyTo` et exécute l'animation.
   *
   * Sync atomique de `lastSearchCenter` vers la destination : un flyTo
   * traduit toujours une intention explicite (URL ville, bouton, etc.),
   * la destination devient donc immédiatement la nouvelle référence du
   * bouton "Search in this area". Évite les races avec moveend.
   */
  flyTo: (lat, lng, zoom = MAP_CONFIG.DEFAULT_ZOOM) => {
    console.log('🗺️ [mapStore] flyTo:', { lat, lng, zoom });
    set({
      pendingFlyTo: { lat, lng },
      zoom,
      isAnimating: true,
      lastSearchCenter: { lat, lng },
      shouldShowSearchButton: false,
    });
  },

  /**
   * Téléportation instantanée vers une position (sans animation).
   * MapLibreView écoute `pendingJumpTo` et exécute le jumpTo.
   * Sync `lastSearchCenter` pour la même raison que `flyTo`.
   */
  jumpTo: (lat, lng, zoom = MAP_CONFIG.DEFAULT_ZOOM) => {
    console.log('🗺️ [mapStore] jumpTo:', { lat, lng, zoom });
    set({
      pendingJumpTo: { lat, lng },
      zoom,
      center: { lat, lng },
      lastSearchCenter: { lat, lng },
      shouldShowSearchButton: false,
    });
  },

  /**
   * Met à jour le centre de la carte (appelé après moveend).
   * Ne déclenche pas d'animation.
   */
  setCenter: (lat, lng) => {
    set({ center: { lat, lng } });
  },

  /**
   * Met à jour le zoom actuel (appelé après zoomend).
   */
  setZoom: (zoom) => {
    set({ zoom });
  },

  /**
   * Indique que la carte est prête (après le chargement initial).
   */
  setReady: (ready) => {
    console.log('🗺️ [mapStore] setReady:', ready);
    set({ isReady: ready });
  },

  /**
   * Définit le centre de la dernière recherche.
   * Utilisé pour calculer si "Search in this area" doit apparaître.
   */
  setSearchCenter: (lat, lng) => {
    console.log('🗺️ [mapStore] setSearchCenter:', { lat, lng });
    set({
      lastSearchCenter: { lat, lng },
      shouldShowSearchButton: false,
    });
  },

  /**
   * Met à jour la visibilité du bouton "Search in this area".
   * Appelé après chaque déplacement de la carte.
   *
   * Garde `isAnimating` : pendant un flyTo programmatique, des moveend
   * intermédiaires peuvent fuir (resize, gesture interruption…) et
   * comparer une position transitoire à `lastSearchCenter` produirait
   * un faux positif. On laisse l'animation finir.
   */
  updateSearchButtonVisibility: (currentLat, currentLng) => {
    const { lastSearchCenter, isAnimating } = get();
    if (isAnimating) return;
    if (!lastSearchCenter) {
      set({ shouldShowSearchButton: false });
      return;
    }

    const distance = calculateDistanceKm(
      currentLat, currentLng,
      lastSearchCenter.lat, lastSearchCenter.lng
    );

    const shouldShow = distance > MAP_CONFIG.SEARCH_DISTANCE_THRESHOLD;

    // Ne mettre à jour que si la valeur change
    if (get().shouldShowSearchButton !== shouldShow) {
      set({ shouldShowSearchButton: shouldShow });
    }
  },

  // ============================================================================
  // ACTIONS INTERNES (appelées par MapLibreView)
  // ============================================================================

  clearPendingFlyTo: () => {
    set({ pendingFlyTo: null });
  },

  clearPendingJumpTo: () => {
    set({ pendingJumpTo: null });
  },

  setAnimating: (animating) => {
    set({ isAnimating: animating });
  },
}));

// ============================================================================
// HOOKS DÉRIVÉS - Pour éviter les re-renders inutiles
// ============================================================================
// Utiliser ces hooks au lieu d'accéder directement au store entier.
// Chaque hook ne déclenche un re-render que si sa valeur spécifique change.

export const useMapReady = () => useMapStore(state => state.isReady);
export const usePendingFlyTo = () => useMapStore(state => state.pendingFlyTo);
export const usePendingJumpTo = () => useMapStore(state => state.pendingJumpTo);
export const useSearchButtonVisible = () => useMapStore(state => state.shouldShowSearchButton);

// Hook pour les actions (utilise useShallow pour éviter les re-renders infinis)
export const useMapActions = () => useMapStore(
  useShallow(state => ({
    flyTo: state.flyTo,
    jumpTo: state.jumpTo,
    setCenter: state.setCenter,
    setZoom: state.setZoom,
    setReady: state.setReady,
    setSearchCenter: state.setSearchCenter,
    updateSearchButtonVisibility: state.updateSearchButtonVisibility,
    clearPendingFlyTo: state.clearPendingFlyTo,
    clearPendingJumpTo: state.clearPendingJumpTo,
    setAnimating: state.setAnimating,
  }))
);
